import { theme } from '@/shared/design-system/theme'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useImpressionTracker from 'react-impression-tracker'
import styled from 'styled-components'
import { createFetchWebEventCollector, EventName, PageName, Predecessor } from 'web-event-collector'
import { IcCheckMarkFilled } from '@healingpaper/cell-icons'
import Image from 'next/image'
import { getEnv } from '@unni/platform'

import { Action, Icon } from '@/components'
import Text from '@/components/atomic/atoms/Text'
import { addGaPageView, postAmplitude, postGA } from '@/core/lib'
import { createCommonEvent } from '@/utils/eventCollector'
import { generateTagId } from '@/utils/test-automation'
import { getResizedImageUrl, ResizableImageFormats } from '@/utils/image-resizer/imageResizer'
import { HospitalInList } from '@/features/hospital/types/hospital.type'
import { useLocales } from '@/shared/hooks/useLocales'
import { Locales } from '@/utils/language'
import FlexCol from '@/components/shared/FlexCol'

const Wrapper = styled.li`
  box-sizing: border-box;
  width: 100%;
  margin: 0px;
  padding-bottom: 20px;
  cursor: pointer;

  img {
    display: block;
  }

  a {
    text-decoration: none;
  }

  span[class^='Icon'] {
    color: #ffd267;
    margin-right: 4px;
  }

  @keyframes gradientBG {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`

interface Props {
  hospital: HospitalInList
  meta?: any
  userId?: number
  indexInList?: number
  indexInPage?: number
  pageName?: PageName.HospitalListWeb | PageName.SearchTotalWeb
  oneColumn?: boolean // HospitalListItem 1단으로 보여줄지에 대한 여부
  onClick?: () => void
  parentPage?: 'wishlist'
}

const HospitalListItem: React.FC<Props> = (props: Props) => {
  const { hospital, onClick } = props
  const { t } = useTranslation()
  const { locale } = useLocales()
  const isJapanUser = locale === Locales.JA_JP
  const isHospitalSupportingJapan = hospital.supportingLangList.includes('ja')
  const isEnglishUser = locale === Locales.EN_US
  const isHospitalSupportingEnglish = hospital.supportingLangList.includes('en')

  const openHospital = (hospitalId: number) => {
    addGaPageView(`/hospitals/${hospitalId}`)
    if (props.parentPage === 'wishlist') {
      postAmplitude('click_hospital_in_wishlist', { hospital_id: props.hospital.id })
    }
  }

  const actionProperties = {
    hospital_id: hospital.id,
    hospital_name: hospital.name,
    hospital_star_point: hospital.rating,
    hospital_comment: hospital.reviewCount,
    sorting_number: typeof props.meta === 'object' ? props.meta.pageIndex : 0,
  }

  const { emitHospitalClicked } = createFetchWebEventCollector({
    baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
  })

  const handleClickHospital = () => {
    openHospital(hospital.id)

    if (props.userId && props.indexInList !== undefined && props.pageName) {
      const commonEvent = createCommonEvent(navigator.userAgent, props.userId)
      const predecessor: Predecessor = {
        Name: EventName.HospitalClicked,
        TraceId: commonEvent.TraceId,
      }
      localStorage.setItem('predecessor', JSON.stringify(predecessor))

      if (props.pageName === PageName.HospitalListWeb) {
        emitHospitalClicked({
          ...commonEvent,
          Name: EventName.HospitalClicked,
          Body: {
            ...commonEvent.Body,
            hospitalId: hospital.id,
            pageName: props.pageName,
            itemOrderInPage: props.indexInList + 1,
          },
        })
      }

      if (props.pageName === PageName.SearchTotalWeb) {
        emitHospitalClicked({
          ...commonEvent,
          Name: EventName.HospitalClicked,
          Body: {
            ...commonEvent.Body,
            hospitalId: hospital.id,
            pageName: props.pageName,
            placementOrderInPage: Number(props.indexInPage) + 1,
            itemOrderInPlacement: props.indexInList + 1,
          },
        })
      }
    }

    onClick?.()
  }

  const [isImpressionVisible, setIsImpressionVisible] = useState(false)

  const { targetRef, startTracking } = useImpressionTracker<HTMLLIElement, object>({
    context: {},
    onImpressed: () => {
      if (props.userId && props.indexInList !== undefined && props.pageName) {
        const commonEvent = createCommonEvent(navigator.userAgent, props.userId)

        const { emitHospitalViewed } = createFetchWebEventCollector({
          baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
        })

        if (props.pageName === PageName.HospitalListWeb) {
          emitHospitalViewed({
            ...commonEvent,
            Name: EventName.HospitalViewed,
            Body: {
              ...commonEvent.Body,
              hospitalId: hospital.id,
              itemOrderInPage: Number(props.indexInPage),
              pageName: props.pageName,
            },
          })

          return
        }

        if (props.pageName === PageName.SearchTotalWeb) {
          emitHospitalViewed({
            ...commonEvent,
            Name: EventName.HospitalViewed,
            Body: {
              ...commonEvent.Body,
              hospitalId: hospital.id,
              placementOrderInPage: Number(props.indexInPage),
              itemOrderInPlacement: props.indexInList,
              pageName: props.pageName,
            },
          })

          return
        }
      }
    },
    isImpressionVisible,
    hasImpressed: false,
    rootMargin: '-66px',
  })

  useEffect(() => {
    localStorage && setIsImpressionVisible(localStorage.getItem('isImpressionVisible') === 'true')
  }, [])

  const hospitalMainImage = hospital.mainImage || hospital.photos[0]

  const HospitalListItemUI = (
    <>
      <StyledHospitalImageBox>
        {hospital.assessmentState === 'CERTIFIED' && (
          <StyledCertifiedHospitalBadge>
            <IcCheckMarkFilled width={16} height={16} color={theme.colors.certified} />
            <span>{t('hospital.certifiedHospital')}</span>
          </StyledCertifiedHospitalBadge>
        )}
        <Image
          src={
            hospitalMainImage
              ? getResizedImageUrl(hospitalMainImage, { format: ResizableImageFormats.WEBP })
              : '/images/hospitals/empty.svg'
          }
          alt={hospital.name}
          sizes="100vw"
          style={{
            width: '100%',
            height: 'auto',
          }}
          width={1080}
          height={420}
          onLoad={() => {
            if (props.userId && props.indexInList !== undefined) {
              startTracking()
            }
          }}
        />
      </StyledHospitalImageBox>

      <FlexCol gap={8}>
        <Text
          type="title3"
          element="h3"
          id={generateTagId('hospital', 'card', 'text', 'title', `${props.indexInList || props.hospital.id}`)}
        >
          {hospital.name}
        </Text>
        {isJapanUser && isHospitalSupportingJapan && (
          <StyledBadge className="label">{t('components.hospitalListItem.contents.ableJapanese')}</StyledBadge>
        )}
        {isEnglishUser && isHospitalSupportingEnglish && (
          <StyledBadge className="label">Supporting English</StyledBadge>
        )}

        <StyledInfo>
          {hospital.reviewCount >= 3 && (
            <>
              <Text type="caption2" element="span" color={theme.colors.gray800}>
                <Icon size={12} name="star" />
                {hospital.rating?.toFixed(1)}
                <span>
                  {` (${t('components.hospitalListItem.contents.rating')}`}
                  <StyledHighlight>{` ${hospital.reviewCount.toLocaleString()}`}</StyledHighlight>
                  {`${t('components.hospitalListItem.contents.unit')})`}
                </span>
              </Text>
              <StyledVerticalDivider>|</StyledVerticalDivider>
            </>
          )}

          {/* 이벤트 수 */}
          <Text type="caption2" element="span" color={theme.colors.gray800}>
            {t('components.hospitalListItem.contents.event')} <StyledHighlight>{hospital.pageCount}</StyledHighlight>
            {t('components.hospitalListItem.contents.unit')}
          </Text>
        </StyledInfo>
      </FlexCol>
    </>
  )

  return (
    <Wrapper aria-label="Hospital Item" ref={targetRef}>
      <Action
        href={`/hospitals/${hospital.id}`}
        newTab
        trackEvent={() => {
          postGA('HOSPITAL_ITEM', 'click', hospital.id)
          postAmplitude('click_hospital_in_list', actionProperties)
        }}
        onClick={handleClickHospital}
      >
        {HospitalListItemUI}
      </Action>
    </Wrapper>
  )
}

const StyledInfo = styled.div`
  display: flex;
  align-items: center;
`
const StyledHighlight = styled.span`
  color: ${({ theme }) => theme.colors.orange};
`
const StyledVerticalDivider = styled.div`
  display: inline-block;
  padding: 0 8px;
  color: ${({ theme }) => theme.colors.gray400};
  font-size: 12px;
`
const StyledBadge = styled.div`
  display: inline-block;
  padding: 5px;
  width: fit-content;
  color: ${({ theme }) => theme.colors.gray800};
  font-size: 11px;
  font-weight: 500;
  line-height: 11px;
  border: ${({ theme }) => `1px solid ${theme.colors.gray400}`};
  border-radius: 4px;
  box-sizing: border-box;
`
const StyledHospitalImageBox = styled.div`
  background: linear-gradient(-45deg, #e0e0e0, #e0e0e0, #f1f1f1, #f1f1f1);
  background-size: 400% 400%;
  animation: gradientBG 1s ease-in infinite;
  width: 100%;
  height: 144px;
  border-radius: 8px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  img {
    position: relative;
    width: 100%;
    min-height: 100%;
    object-fit: cover;
  }
`
const StyledCertifiedHospitalBadge = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 5px 8px 5px 4px;

  color: #4c6ef5;
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;

  background-color: white;
  border: 1px solid #d6d6d6;
  box-sizing: border-box;
  border-radius: 4px;

  span {
    font-size: 10px;
    font-weight: 500;
    line-height: 10px;
    vertical-align: middle;
  }
`
export default HospitalListItem
